import * as React from 'react';

import {
  Button,
  DialogContent,
  DialogHeader,
  DialogOverlay,
} from '@rematter/ui-web';
import { LucideAlertTriangle, LucideTrash2 } from 'lucide-react';

import { useTranslations } from '../../i18n/useTranslations';

type LotDeallocateConfirmDialogProps = {
  isOpen: boolean;
  count: number;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
};

export const LotDeallocateConfirmDialog: React.FC<
  LotDeallocateConfirmDialogProps
> = ({ isOpen, count, onConfirm, onCancel }) => {
  const t = useTranslations();
  const [isLoading, setIsLoading] = React.useState(false);

  const handleConfirm = React.useCallback(async () => {
    setIsLoading(true);
    try {
      await onConfirm();
    } finally {
      setIsLoading(false);
    }
  }, [onConfirm]);

  if (!isOpen) return null;

  return (
    <DialogOverlay
      isOpen
      isDismissable
      onOpenChange={(open) => !open && onCancel()}
      className="@LotDeallocateConfirmDialog"
    >
      <DialogContent onOpenChange={(open: boolean) => !open && onCancel()}>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <LucideAlertTriangle className="size-5 text-warning" />
            <span className="text-base font-semibold">
              {t.lotsListView.detail.deallocateConfirm(count)}
            </span>
          </div>
        </DialogHeader>
        <div className="flex flex-col gap-2 px-4">
          <div className="text-base text-neutral/50">
            {t.lotsListView.detail.deallocateConfirmDescription}
          </div>
          <div className="flex justify-end gap-2 py-4">
            <Button
              variant="text"
              color="neutral"
              onPress={onCancel}
              isDisabled={isLoading}
            >
              {t.lotsListView.dialog.cancel}
            </Button>
            <Button
              variant="solid"
              color="failure"
              icon={<LucideTrash2 className="size-4" />}
              onPress={handleConfirm}
              isLoading={isLoading}
            >
              {t.lotsListView.detail.deallocate}
            </Button>
          </div>
        </div>
      </DialogContent>
    </DialogOverlay>
  );
};
